class PaymentOptions extends HTMLElement {
  KLARNA_TAG = 'klarna-placement';
  AFTERPAY_TAG = 'square-placement';
  SHOPPAY_TAG = 'shopify-payment-terms';

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    this.currentVariantId = this.getAttribute('current-variant-id');
    this.currentVariantPrice = this.getAttribute('current-variant-price');

    this.availablePaymentOptions = {
      klarna: {
        enabled: false,
        installments: null,
        pricePerInstallment: null,
      },
      afterpay: {
        enabled: false,
        installments: null,
        pricePerInstallment: null,
      },
      shoppay: {
        enabled: false,
        installments: null,
        pricePerInstallment: null,
      },
    };

    this.shadow.innerHTML = `
        <style>
          .payment-options {
            min-height: 33px;
          }

          .klarna-button,
          .afterpay-button,
          .shop-pay-button {
            cursor: pointer;
            vertical-align: middle;
            background-color: transparent;
            border: none;
            border-radius: 0;
            padding: 0;
            margin: 0;
          }

          .klarna-button img,
          .afterpay-button img,
          .shop-pay-button img {
            width: 100%;
            height: auto;
          } 
        </style>
      `;
  }

  get klarna() {
    return document.querySelector(this.KLARNA_TAG);
  }

  get afterPay() {
    return document.querySelector(this.AFTERPAY_TAG);
  }

  get shopPay() {
    return document.querySelector(this.SHOPPAY_TAG);
  }

  async connectedCallback() {
    this.observeInjectedComponents();
  }

  observeInjectedComponents() {
    const observer = new MutationObserver(() => {
      if (this.areComponentsReady()) {
        observer.disconnect();
        // Now that they exist *and* have shadow content:
        this.updatePaymentOptions();
        this.render();
      }
    });

    // Start watching document body for injected elements
    observer.observe(document.body, { childList: true, subtree: true });
  }

  areComponentsReady() {
    const klarnaEl = document.querySelector(this.KLARNA_TAG);
    const afterPayEl = document.querySelector(this.AFTERPAY_TAG);
    // Shadow roots must exist *and* contain content
    const hasKlarnaContent = klarnaEl?.shadowRoot?.innerHTML?.trim();
    const hasAfterPayContent = afterPayEl?.shadowRoot?.innerHTML?.trim();

    const hasKlarnaPlacementAttributes = klarnaEl?.placementAttributes?.mainText;

    return hasKlarnaContent && hasAfterPayContent && hasKlarnaPlacementAttributes;
  }

  updatePaymentOptions() {
    this.updateKlarnaTerms();
    this.updateAfterPayTerms();
    this.updateShopPayTerms();
  }

  /**
   * Build the template for the payment options based on the available payment options
   *   Klarna, AfterPay and ShopPay (4 payments)
   *   4 interest-free payments of $X.XX with <afterpay>, <klarna>, or <ShopPay>

   *   Klarna and AfterPay (4 payments). ShopPay (12 installments with interest)
   *   4 interest-free payments of $X.XX with <afterpay> and <klarna>, or from $X.XX/mo. with <ShopPay>

   *   Klarna and AfterPay (4 payments)
   *   4 interest-free payments of $X.XX with <afterpay> or <klarna>
   
   *   Klarna and AfterPay (12 installments with interest)
   *   from $X.XX/mo. with <afterpay> or from $X.XX/mo. with <klarna>

   *   Non-USA (International)
   *   Pay with interest-free installments with <aftepay> or <klarna>
   */
  buildTemplate() {
    // Figure out which scenario we’re in
    const scenario = this.computeScenario();
    // Return the appropriate template
    return this.buildMessage(scenario);
  }

  computeScenario() {
    const {
      klarna: { enabled: kEnabled, installments: kInst },
      afterpay: { enabled: aEnabled, installments: aInst },
      shoppay: { enabled: sEnabled, installments: sInst, isProductPriceTooLow: isShopPayProductPriceTooLow },
    } = this.availablePaymentOptions;

    // If the country is not the US, return the international scenario
    if (Shopify.country !== 'US') return 'INTERNATIONAL';

    // IF Klarna, Afterpay and ShopPay are enabled
    if (kEnabled && aEnabled && sEnabled) {
      if (kInst === 4 && aInst === 4 && sInst === 4) {
        return 'K-A-SH#4';
      }

      if (kInst === 4 && aInst === 4 && sInst !== 2 && sInst !== 4) {
        return 'K-A#4__SH#INTEREST';
      }

      if (kInst === 4 && aInst === 4 && sInst === 2) {
        return 'K-A#4__SH#2';
      }
    }

    // IF Klarna and Afterpay are enabled but ShopPay is not
    if (kEnabled && aEnabled) {
      if (kInst === 4 && aInst === 4 && isShopPayProductPriceTooLow) {
        return 'K-A#4__SH#TOOLOW';
      }

      if (kInst === 4 && aInst === 4) {
        return 'K-A#4';
      }
    }

    return 'FALLBACK';
  }

  buildMessage(scenario) {
    const shopPayPrice = this.availablePaymentOptions.shoppay.pricePerInstallment;
    const shopPayMinPrice = this.availablePaymentOptions.shoppay.minPrice;

    const afterPayPrice = this.availablePaymentOptions.afterpay.pricePerInstallment;
    const klarnaPrice = this.availablePaymentOptions.klarna.pricePerInstallment;

    switch (scenario) {
      case 'K-A-SH#4':
        return `
            <div class="payment-options">
              4 interest-free payments of ${shopPayPrice} with <afterpay> <klarna> or <shopPay>
            </div>
          `;
      case 'K-A#4':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> or <klarna>
            </div>
          `;
      case 'K-A#4__SH#INTEREST':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> and <klarna>
              or from ${shopPayPrice}/mo. with <shopPay>
            </div>
          `;
      case 'K-A#4__SH#2':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> or <klarna> or 2 interest-free installments of ${shopPayPrice} with <shopPay>
            </div>`;
      case 'K-A#4__SH#TOOLOW':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> or <klarna> or pay over time for orders over ${shopPayMinPrice} with <shopPay>
            </div>
          `;
      case 'INTERNATIONAL':
        return `
            <div class="payment-options">
              Pay with interest-free installments with <afterpay> or <klarna>
            </div>
          `;
      default:
        return `
            <div class="payment-options">
              Pay with interest-free installments with <afterpay> or <klarna>
            </div>
          `;
    }
  }

  getPaymentLogo(paymentOption) {
    switch (paymentOption) {
      case this.KLARNA_TAG:
        return `
            <button id="payment-options-klarna-button" class="klarna-button" style="width: 50px">
              <img src="https://cdn.shopify.com/s/files/1/0747/5039/files/klarna-logo.png" alt="" />
            </button>`;
      case this.AFTERPAY_TAG:
        return `
            <button id="payment-options-afterpay-button" class="afterpay-button" style="width: 80px">
              <img src="https://cdn.shopify.com/s/files/1/0747/5039/files/afterpay-logo.svg" alt="" />
            </button>`;
      case this.SHOPPAY_TAG:
        return `
            <button id="payment-options-shop-pay-button" class="shop-pay-button" style="width: 60px">
              <img src="https://cdn.shopify.com/s/files/1/0747/5039/files/shopPay_logo.png" alt="" />
            </button>`;
      default:
        return '';
    }
  }

  updateKlarnaTerms() {
    const mainText = this.klarna.placementAttributes.mainText;
    // Usually it returns something like '4 interest-free payments of $42.00 with'
    // '4 interest-free payments of $42.00 with'

    const price = this.getPriceFromText(mainText);
    const installments = this.getInstallmentsFromText(mainText);

    this.availablePaymentOptions.klarna.enabled = true;
    this.availablePaymentOptions.klarna.installments = installments;
    this.availablePaymentOptions.klarna.pricePerInstallment = price;
  }

  updateAfterPayTerms() {
    const mainText = this.afterPay.getMainText();
    // Usually it returns something like 'or 4 interest-free payments of $42.00 with'
    // We need to extract the price and the number of installments

    const price = this.getPriceFromText(mainText);
    const installments = this.getInstallmentsFromText(mainText);

    this.availablePaymentOptions.afterpay.enabled = true;
    this.availablePaymentOptions.afterpay.installments = installments;
    this.availablePaymentOptions.afterpay.pricePerInstallment = price;
  }

  updateShopPayTerms() {
    if (!this.shopPay) return;

    const meta = JSON.parse(this.shopPay.getAttribute('shopify-meta'));
    const currentVariant = meta.variants.find((variant) => variant.id == this.currentVariantId);

    this.availablePaymentOptions.shoppay.enabled = currentVariant?.eligible;
    this.availablePaymentOptions.shoppay.minPrice = meta.min_price;
    this.availablePaymentOptions.shoppay.maxPrice = meta.max_price;

    if (this.currentVariantPrice < this.parsePrice(meta.min_price)) {
      this.availablePaymentOptions.shoppay.isProductPriceTooLow = true;
      return;
    }

    if (this.currentVariantPrice > this.parsePrice(meta.max_price)) {
      this.availablePaymentOptions.shoppay.isProductPriceTooHigh = true;
      return;
    }

    let isEligibleFor12Months = false;
    let aPr = 0;

    meta.financing_plans.forEach((plan) => {
      if (
        this.currentVariantPrice >= this.parsePrice(plan.min_price) &&
        this.currentVariantPrice <= this.parsePrice(plan.max_price)
      ) {
        plan.terms.forEach((term) => {
          if (term.installments_count === 12) {
            isEligibleFor12Months = true;
            aPr = term.apr;
          }
        });
      }
    });

    if (isEligibleFor12Months) {
      this.availablePaymentOptions.shoppay.installments = 12;

      const calculatedInstallment = this.calculateInstallment(this.currentVariantPrice, aPr, 12);
      this.availablePaymentOptions.shoppay.pricePerInstallment = this.formatPrice(calculatedInstallment.toString());
    } else {
      this.availablePaymentOptions.shoppay.installments = currentVariant.number_of_payment_terms;
      this.availablePaymentOptions.shoppay.pricePerInstallment = currentVariant.price_per_term;
    }
  }

  parsePrice(price) {
    return parseFloat(price.replace(/[^0-9.]/g, ''));
  }

  formatPrice(price) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    return formatter.format(price);
  }

  getPriceFromText(text) {
    const match = text.match(/\$\d+\.\d+/);
    const price = match ? match[0] : null;
    return price;
  }

  getInstallmentsFromText(text) {
    const match = text.match(/\d+/);
    const monthCount = match ? parseInt(match[0], 10) : null;
    return monthCount;
  }

  calculateInstallment(price, apr, months) {
    // Convert APR to a monthly interest rate (decimal form)
    const monthlyInterestRate = apr / 100 / 12;

    // If there's no interest (i.e., apr = 0), just return price / months
    if (monthlyInterestRate === 0) {
      return parseFloat((price / months).toFixed(2));
    }

    // Apply the standard formula for installment calculation
    const numerator = price * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, months);
    const denominator = Math.pow(1 + monthlyInterestRate, months) - 1;
    const monthlyPayment = numerator / denominator;

    // Return result rounded to two decimals
    return parseFloat(monthlyPayment.toFixed(2));
  }

  attachEventListeners() {
    const klarnaButton = this.shadow.querySelector('#payment-options-klarna-button');
    const afterpayButton = this.shadow.querySelector('#payment-options-afterpay-button');
    const shopPayButton = this.shadow.querySelector('#payment-options-shop-pay-button');

    klarnaButton.addEventListener('click', () => {
      const klarnaButton = this.klarna.shadowRoot.querySelector('button.link');
      klarnaButton.dispatchEvent(new Event('click'));
    });

    afterpayButton.addEventListener('click', () => {
      if (AfterPay) {
        AfterPay.launchModal();
      }
    });

    shopPayButton.addEventListener('click', () => {
      this.shopPay.shadowRoot.querySelector('#shopify-installments-cta').dispatchEvent(new Event('click'));
    });
  }

  render() {
    const template = this.buildTemplate();

    const updatedTemplate = template
      .replace('<afterpay>', this.getPaymentLogo(this.AFTERPAY_TAG))
      .replace('<klarna>', this.getPaymentLogo(this.KLARNA_TAG))
      .replace('<shopPay>', this.getPaymentLogo(this.SHOPPAY_TAG));

    this.shadow.innerHTML += updatedTemplate;

    if (Shopify.country === 'US') {
      this.attachEventListeners();
    }
  }
}

customElements.define('payment-options', PaymentOptions);